import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';
import Thumbnail from '@/components/Thumbnail/Thumbnail.vue';
import SelectPlanting from '@/components/SelectPlanting/SelectPlanting.vue';
import Datepicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale'
import WorkItemSelectModal from "@/assets/common/Modal/ItemSelectModal/WorkItemModal/WorkItemSelectModal.vue";
import Modal from "@/assets/common/Modal/Modal.vue";
import MachineSelectModal from "@/assets/common/Modal/ItemSelectModal/MachineSelectModal/MachineSelectModal.vue";
import {Item, Machine, WorkItem} from '@/types';

interface Planting { // 作付全体
    id: string;
    name: string;
    agri_fields: Array<{ id: string, name: string, planted_area: number }> | null;
    crop: {
        id: string;
        name: string;
        phonetic: string;
    };
    memo: string | null;
    work_start_date: string | null;
    work_end_date: string | null;
    selected_works: Array<SelectedWorkDetail>;
    per_type: string | null; // 10aあたりor総時間
    total_yield: number | null; // 合計収量
    weight_unit: { // 単位
        id: string | null; // 単位のID
        name: string; // 単位の名前
    };
    shipping_start_date: string | null; // 出荷開始日
    shipping_end_date: string | null; // 出荷終了日
}

interface Season {
    tagId: string;
    selectedWorkId: string;
    startDate: Date;
    endDate: Date;
    withinPeriod: boolean;
}

interface SelectedWorkDetail {
    work_item: Item;
    update_type: string | undefined;
    plan_type: string; // 経過日数or期間
    working_start_date: string | null; // 期間のスタート
    working_end_date: string | null; // 期間のエンド
    selected_work_item_id: string | null; // 相対日数を出したい作業項目のID
    days: number | null; // 相対日数
    ago_or_later: string | null; // 日前or日後
    setting_type: string | null; // 合計時間or月別or旬別
    total_time: number | null; // 合計時間
    per_type: string; // 10aあたりor総時間
    attention_point: string;
    image_id: string[];
    time_allocation: TimeAllocation[];
    related_machines: Machine[];
}

interface TimeAllocation {
    working_period: string;
    working_hour: number;
}

interface WorkItemImage {
    id: string;
    url: string;
    file: File | null;
}

interface WeightUnit {
    id: string;
    name: string;
    weight_per_unit: number;
    is_hidden: boolean;
}

@Component({
    components: {
        'vuejs-datepicker': Datepicker,
        Thumbnail,
        SelectPlanting,
        WorkItemSelectModal,
        MachineSelectModal,
        Modal
    },
})
export default class CultivationPlan extends Vue {

    @Prop({
        required: true
    })
    private planting!: Planting;

    private weightUnits: Array<WeightUnit> = []
    private defaultWeightUnit = {
        id: null,
        name: 'kg',
        weight_per_unit: 1
    }
    private defaultPerType = '10a'
    private showSelectPlanting = false
    private isLoading = false
    private selectedWorkItems: Item[] = []

    private scheduleWidth = 400;

    datePickerFormat = 'yyyy-MM-dd';
    ja = ja

    isWorkItemModalVisible = false
    workItemDetailModal = false

    initWorkItemsId: string[] = []
    initWorkItemImages: Array<{ workItemId: string, fileId: string[] }> = []
    newWorkDetails: SelectedWorkDetail[] = []
    editingDetail: SelectedWorkDetail = {
        work_item: {
            id: '',
            name: '',
            is_hidden: false
        },
        update_type: undefined,
        plan_type: 'PERIOD',
        working_start_date: null,
        working_end_date: null,
        selected_work_item_id: '',
        days: null,
        ago_or_later: '日後',
        setting_type: '合計時間',
        total_time: null,
        per_type: '10a',
        attention_point: '',
        image_id: [],
        time_allocation: [],
        related_machines: []
    }
    editingImages: WorkItemImage[] = []
    uploadImages: Array<{ workItemId: string, file: File | null }> = []
    deletedImages: Array<{ workItemId: string, fileId: string }> = []
    editingImageMap: Map<string, WorkItemImage[]> = new Map()

    // マウント時
    mounted(): void {
        this.init()
        window.addEventListener('resize', this.handleResize)
        this.handleResize()
    }
    private beforeDestroy(): void {
        window.removeEventListener('resize', this.handleResize)
    }

    handleResize(): void {
        const swrp = document.getElementById("wrap_schedule_select")?.clientWidth;
        this.scheduleWidth = swrp ? swrp : 400;
    }


    async init(): Promise<void> {
        this.initWorkItemsId = this.planting.selected_works.map(sw => sw.work_item.id)
        this.planting.selected_works.forEach(sw => {
            if (sw.image_id.length > 0) {
                const image = {
                    workItemId: sw.work_item.id,
                    fileId: sw.image_id
                }
                this.initWorkItemImages.push(image)
            }
        })
        this.newWorkDetails = this.sortWorkDetails(this.planting.selected_works.map((sw) => {
            const timeAllocation = sw.time_allocation.sort((a, b) => {
                if(a.working_period.slice(0, -2) === b.working_period.slice(0, -2)) {
                    switch (a.working_period.slice(-2)) {
                        case '上旬':
                            return -1
                        case '中旬':
                            return b.working_period.slice(-2) === '上旬' ? 1 : -1
                        case '下旬':
                            return 1
                        default:
                            return 1
                    }
                } else {
                    return 1
                }
            })
            return {
                ...sw, time_allocation: timeAllocation, update_type: 'UPDATE'
            }
        }))
        this.weightUnits = await this.getWeightUnits()
        if (!this.planting.per_type) {
            this.planting.per_type = this.defaultPerType
        }
        if (!this.planting.weight_unit || !this.planting.weight_unit.id) {
            this.planting.weight_unit = this.defaultWeightUnit
        }
    }

    // 一覧取得関係
    async getWorkItemImage(plantingId: string, workItemId: string, fileId: string): Promise<WorkItemImage> {
        const res = await this.$apiClient.doRequest('get', `/plantings/${plantingId}/plans/${workItemId}/images/${fileId}`)
        return res
    }

    async getWeightUnits(): Promise<WeightUnit[]> {
        const res = await this.$apiClient.doRequest('get', '/weight_units?with_hidden=true')
        return res.weight_units
    }

    get filterWeightUnits() {
        return this.weightUnits
            .filter(wu => this.planting.weight_unit.id == wu.id || wu.is_hidden === false)
    }

    getPlantingDetail(plantingId: string): void {
        this.$apiClient.doRequest('get', `/plantings/${plantingId}`)
            .then((res: Planting) => {
                this.planting.work_start_date = res.work_start_date
                this.planting.work_end_date = res.work_end_date
                this.planting.selected_works = res.selected_works
                this.planting.per_type = res.per_type ? res.per_type : this.defaultPerType
                this.planting.total_yield = res.total_yield
                if (res.weight_unit && res.weight_unit.id) {
                    this.planting.weight_unit = res.weight_unit
                } else {
                    this.planting.weight_unit = this.defaultWeightUnit
                }
                this.planting.shipping_start_date = res.shipping_start_date
                this.planting.shipping_end_date = res.shipping_end_date
                this.initWorkItemsId = res.selected_works.map(sw => sw.work_item.id)
                this.newWorkDetails = res.selected_works
            })
    }

    /**
     *
     * @param isEndDate 終了日の設定か否か
     * @param dateString 基準日付：開始日の設定では終了日を指定、終了日の設定では開始日を指定する
     * @param settingType 合計時間|月別|旬別
     */
    setDisabledDates(isEndDate: boolean, dateString: string, settingType?: string): {
        to?: Date | undefined;
        from?: Date | undefined;
        customPredictor?: ((date: Date) => boolean) | undefined;
    } {
        const options: {
            to?: Date,
            from?: Date,
            customPredictor?: (date: Date) => boolean
        } = {}
        if (isEndDate && dateString) {
            // 終了日の設定
            // 開始日以降のみ選択可能
            options.to = new Date(dateString)
        } else if (dateString) {
            // 開始日の設定
            // 終了日以前のみ選択可能
            options.from = new Date(dateString)
        }

        // 作業項目詳細設定用
        switch (settingType) {
            case '月別':
                options.customPredictor = (date) => {
                    if (isEndDate) {
                        // 月末以外無効
                        return date.getDate() != new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                    } else {
                        // 1日以外無効
                        return date.getDate() != 1
                    }
                }
                break
            case '旬別':
                options.customPredictor = (date) => {
                    if (isEndDate) {
                        // 10日 or 20日 or 月末以外無効
                        switch (date.getDate()) {
                            case 10:
                                return false
                            case 20:
                                return false
                            case new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate():
                                return false
                            default:
                                return true
                        }
                    } else {
                        // 1日 or 11日 or 21日以外無効
                        const today = date.getDate()
                        return today != 1 && today != 11 && today != 21
                    }
                }
                break
        }
        return options
    }


    // 作業者選択モーダル関連
    async openSelectWorkItemModal(): Promise<void> {
        this.selectedWorkItems = this.newWorkDetails.map(sw => sw.work_item)
        this.isWorkItemModalVisible = true;
    }

    onCloseSelectWorkItemModal(value: WorkItem[]): void {
        // 名前が変更された作業項目を反映する
        this.newWorkDetails
            .forEach(workDetail => {
                const newValue = value.find(item => item.id === workDetail.work_item.id)
                if (newValue) {
                    Object.assign(workDetail.work_item, newValue);
                }
            })

        // チェックが外れたものを削除
        this.newWorkDetails
            .filter(workDetail => !value.some(item => item.id === workDetail.work_item.id))
            .forEach((workDetail) => {
                const idx = this.newWorkDetails.indexOf(workDetail);
                this.newWorkDetails.splice(idx, 1);
            });

        // 新しくチェックが付いたものの追加
        const newWorks: SelectedWorkDetail[] = value
            .filter(wi => !this.newWorkDetails.some(sw => wi.id === sw.work_item.id))
            .map(wi => ({
                work_item: wi,
                update_type: this.initWorkItemsId.find(iwii => wi.id === iwii) === undefined ? 'ADD' : 'UPDATE',
                plan_type: 'PERIOD',
                working_start_date: null, // 期間のスタート
                working_end_date: null, // 期間のエンド
                selected_work_item_id: null, // 相対日数を出したい作業項目のID
                days: null, // 相対日数
                ago_or_later: '日後', // 日前or日後
                setting_type: '合計時間', // 合計時間or月別or旬別
                total_time: null, // 合計時間
                per_type: '10a', // 10aあたりor総時間
                attention_point: '',
                image_id: [],
                time_allocation: [],
                related_machines: []
            }))

        this.newWorkDetails.push(...newWorks);
    }

    // 作業項目に関する詳細設定モーダル関連
    async openWorkItemDetailModal(selectedWork: SelectedWorkDetail): Promise<void> {
        this.editingImages = []
        this.editingDetail = Object.assign({}, selectedWork)
        const workItemId = this.editingDetail.work_item.id
        const editingImages = this.editingImageMap.get(workItemId)
        if (editingImages) {
            this.editingImages = editingImages
        } else if (selectedWork.image_id.length > 0) {
            selectedWork.image_id.forEach(fileId => {
                this.getWorkItemImage(this.planting.id, workItemId, fileId).then((image) => {
                    this.editingImages.push(image)
                })
            })
        } else {
            this.editingImages = []
        }
        this.workItemDetailModal = true;
    }

    get termItems(): SelectedWorkDetail[] {
        return this.newWorkDetails.filter(
            sw => sw.plan_type === 'PERIOD'
                && this.editingDetail.work_item.id !== sw.work_item.id
                && sw.working_start_date !== null
                && sw.working_end_date !== null
        )
    }

    get isSelectedCultivateTerm(): boolean {
        return this.planting.work_start_date != null && this.planting.work_end_date != null
    }

    get isSelectedShippingTerm(): boolean {
        return this.planting.shipping_start_date != null && this.planting.shipping_end_date != null
    }

    itemDelete(selectedWork: SelectedWorkDetail): void {
        const swi = this.newWorkDetails.find(nwd => nwd.selected_work_item_id === selectedWork.work_item.id)
        if (swi !== undefined) {
            window.alert('栽培計画の' + swi.work_item.name + 'から「経過日数で指定」で関連づけられているため削除出来ません。')
        } else {
            this.newWorkDetails = this.newWorkDetails.filter(sw => selectedWork.work_item.id !== sw.work_item.id)
            // 添付画像を空にする
            this.editingImageMap.set(selectedWork.work_item.id, [])
        }
    }

    checkWorkItem(wii: string, e: InputEvent): void {
        if (e.target instanceof HTMLInputElement) {
            if (!e.target.checked) {
                const swi = this.newWorkDetails.find(nwd => nwd.selected_work_item_id === wii)
                if (swi !== undefined) {
                    window.alert('栽培計画の' + swi.work_item.name + 'から「経過日数で指定」で関連づけられているため削除出来ません。')
                    e.preventDefault()
                }
            }
        }
    }

    onCloseWorkItemDetailModal(): boolean {
        const errmsg = this.validationWorkItemDetail(this.editingDetail)
        if(errmsg.length > 0) {
            window.alert(errmsg.join('\n'))
            this.workItemDetailModal = true
            return false
        }
        this.editingDetail.time_allocation = this.editingDetail.time_allocation.map(record => {
            return {
                working_period: record.working_period,
                working_hour: record.working_hour? record.working_hour : 0
            }
        })

        const updatedWorkDetails = this.newWorkDetails.map(nwd => (
            nwd.work_item.id === this.editingDetail.work_item.id
                ? { ...this.editingDetail }
                : nwd
        ))
        // 作業項目ごとに画像選択状態を保存する
        this.editingImageMap.set(this.editingDetail.work_item.id, this.editingImages)
        this.newWorkDetails = this.sortWorkDetails(updatedWorkDetails)
        this.workItemDetailModal = false;

        return true
    }

    validationWorkItemDetail(selectedWorkDetail: SelectedWorkDetail): string[] {
        const errmsg: string[] = []
        if (!selectedWorkDetail.plan_type) {
            errmsg.push("栽培計画の作業項目に期間か経過日数を設定してください。")
        }

        if (selectedWorkDetail.plan_type == 'PERIOD') {
            if (selectedWorkDetail.working_start_date == null || selectedWorkDetail.working_end_date == null) {
                if (selectedWorkDetail.working_start_date && selectedWorkDetail.working_end_date == null) {
                    errmsg.push("栽培計画の作業項目に作業期間の終了日を設定してください。")
                } else if (selectedWorkDetail.working_start_date == null && selectedWorkDetail.working_end_date) {
                    errmsg.push("栽培計画の作業項目に作業期間の開始日を設定してください。")
                } else {
                    errmsg.push("栽培計画の作業項目に作業期間を設定してください。")
                }
            } else if (selectedWorkDetail.working_start_date > selectedWorkDetail.working_end_date) {
                errmsg.push("作業期間の開始日は終了日以前で設定してください。")
            }
        }

        if (selectedWorkDetail.plan_type == 'ELAPSED_DAYS') {
            if (!selectedWorkDetail.selected_work_item_id) {
                errmsg.push("経過日数の項目を選択してください。")
            }
            if (!selectedWorkDetail.days) {
                errmsg.push("経過日数を入力してください。")
            } else if (selectedWorkDetail.days < 0) {
                errmsg.push("経過日数は0以上を入力してください。")
            }
            if (!selectedWorkDetail.ago_or_later) {
                errmsg.push("経過日数の前後を選択してください。")
            }
        }

        if(selectedWorkDetail.total_time && selectedWorkDetail.total_time < 0) {
            errmsg.push("合計作業時間は0以上を入力してください。")
        }

        if(selectedWorkDetail.time_allocation && selectedWorkDetail.time_allocation.length > 0) {
            selectedWorkDetail.time_allocation.forEach(record => {
                if(record.working_hour < 0) {
                    errmsg.push(`作業時間配分の時間は0以上を入力してください。(${record.working_period})`)
                }
            })
        }
        return errmsg
    }

    culcTimeAllocations(): TimeAllocation[] {
        if (
            this.editingDetail.plan_type === 'PERIOD' &&
            this.editingDetail.working_start_date !== null &&
            this.editingDetail.working_end_date !== null
        ) {
            const list: Array<string> = []
            const start = moment(this.editingDetail.working_start_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            const end = moment(this.editingDetail.working_end_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            if (this.editingDetail.setting_type === '月別') {
                while (start.diff(end) <= 0) {
                    list.push(start.format('YYYY年MM月'))
                    start.add(1, 'months')
                }
            } else if (this.editingDetail.setting_type === '旬別') {
                while (start.diff(end) <= 0) {
                    if (start.date() <= 10) {
                        list.push(start.format('YYYY年MM月上旬'))
                        start.date(11)
                    } else if (start.date() <= 20) {
                        list.push(start.format('YYYY年MM月中旬'))
                        start.date(21)
                    } else {
                        list.push(start.format('YYYY年MM月下旬'))
                        start.add(1, 'months').date(1)
                    }
                }
            }
            const baseTime = this.editingDetail.total_time ? Math.floor(this.editingDetail.total_time / list.length) : 0
            const plusTime = this.editingDetail.total_time ? this.editingDetail.total_time % list.length : 0
            return list.map((l, i) => ({
                working_period: l,
                working_hour: i < plusTime ? baseTime + 1 : baseTime,
            }))
        } else {
            return []
        }
    }

    // 関連付け関係
    workDetailToAddMachines: SelectedWorkDetail | null = null;
    isRelativeMachineModalVisible = false
    selectedRelativeMachines: Item[] = []

    clickRelativeSelect(workDetail: SelectedWorkDetail): void {
        this.workDetailToAddMachines = this.workDetailToAddMachines === workDetail ? null : workDetail
    }

    isOpenRelativeSelect(workDetail: SelectedWorkDetail): boolean {
        return this.workDetailToAddMachines === workDetail;
    }

    async openRelativeMachineModal(selectedWork: SelectedWorkDetail): Promise<void> {
        this.editingDetail = selectedWork
        this.selectedRelativeMachines = this.editingDetail.related_machines;
        this.isRelativeMachineModalVisible = true
    }

    onCloseRelativeMachineModal(value: Machine[]): void {
        this.editingDetail.related_machines
            .filter(machine => !value.some(item => item.id === machine.id))
            .forEach(machine => {
                const idx = this.editingDetail.related_machines.indexOf(machine)
                this.editingDetail.related_machines.splice(idx, 1)
            })

        const newMachines = value
            .filter(item => !this.editingDetail.related_machines.some(machine => machine.id === item.id));

        this.workDetailToAddMachines!.related_machines.push(...newMachines);
        this.isRelativeMachineModalVisible = false
        this.workDetailToAddMachines = null
    }

    deleteRelativeMachine(machineToDelete: Machine, sw: SelectedWorkDetail): void {
        const idx = sw.related_machines.indexOf(machineToDelete)
        sw.related_machines.splice(idx, 1)
    }

    // 期間指定関係
    get yearList(): Record<string, number> {
        const today = new Date();
        const newYear = new Date(today.getFullYear(), 0, 1, 9, 0)
        const lastYear = new Date(today.getFullYear(), 11, 31, 23, 59)
        const defaultStartDate = (this.planting['work_start_date'] === null) ? newYear : (this.planting['work_start_date'])
        const workItemStartList = this.newWorkDetails.filter(nwd => nwd.working_start_date !== null).map(nwd => nwd.working_start_date)
        workItemStartList.sort()
        const firstStartDate = workItemStartList.length === 0 ? defaultStartDate : workItemStartList[0]
        const startDate = moment(firstStartDate).isBefore(moment(defaultStartDate)) ? firstStartDate : defaultStartDate

        const defaultEndDate = (this.planting['work_end_date'] === null) ? lastYear : (this.planting['work_end_date'])
        const workItemEndList = this.newWorkDetails.filter(nwd => nwd.working_end_date !== null).map(nwd => nwd.working_end_date)
        workItemEndList.sort()
        const lastEndDate = workItemEndList.length === 0 ? defaultEndDate : workItemEndList[workItemEndList.length - 1]
        const endDate = moment(lastEndDate).isAfter(defaultEndDate) ? lastEndDate : defaultEndDate


        // const start = moment(`${startDate.getFullYear()}0101`, "YYYYMMDD")
        // const end = moment(`${endDate.getFullYear()}0101`, "YYYYMMDD")
        const start = moment(startDate)
        const end = moment(endDate)

        const yearList: Array<string> = []
        while (start.diff(end) <= 0) {
            yearList.push(start.format('YYYY'))
            start.add(1, 'months')
        }

        // 年列の長さを算出
        const yearMap: Record<string, number> = {}
        Array.from(new Set(yearList)).map(y1 => {
            yearMap[y1] = yearList.filter(y2 => (
                y2 === y1
            )).length
        })
        return yearMap
    }

    get monthList(): string[] {
        const monthList: Array<string> = []

        const today = new Date();
        const newYear = new Date(today.getFullYear(), 0, 1, 9, 0)
        const lastYear = new Date(today.getFullYear(), 11, 31, 23, 59)
        const defaultStartDate = (this.planting['work_start_date'] === null) ? newYear : (this.planting['work_start_date'])
        const workItemStartList = this.newWorkDetails.filter(nwd => nwd.working_start_date !== null).map(nwd => nwd.working_start_date)
        workItemStartList.sort()
        const firstStartDate = workItemStartList.length === 0 ? defaultStartDate : workItemStartList[0]
        const startDate = moment(firstStartDate).isBefore(moment(defaultStartDate)) ? firstStartDate : defaultStartDate

        const defaultEndDate = (this.planting['work_end_date'] === null) ? lastYear : (this.planting['work_end_date'])
        const workItemEndList = this.newWorkDetails.filter(nwd => nwd.working_end_date !== null).map(nwd => nwd.working_end_date)
        workItemEndList.sort()
        const lastEndDate = workItemEndList.length === 0 ? defaultEndDate : workItemEndList[workItemEndList.length - 1]
        const endDate = moment(lastEndDate).isAfter(defaultEndDate) ? lastEndDate : defaultEndDate

        const start = moment(startDate)
        const end = moment(endDate)

        while (start.diff(end) <= 0) {
            monthList.push(start.format('MM'))
            start.add(1, 'months')
        }

        return monthList
    }

    get seasonList(): {startDate: Date, endDate: Date}[] {
        const length = this.monthList.length * 3;
        const today = new Date();
        const newYear = new Date(today.getFullYear(), 0, 1, 9, 0)
        const defaultStartDate = (this.planting['work_start_date'] === null) ? newYear : (this.planting['work_start_date'])
        const workItemStartList = this.newWorkDetails.filter(nwd => nwd.working_start_date !== '').map(nwd => nwd.working_start_date)
        workItemStartList.sort()
        const firstStartDate = workItemStartList.length === 0 ? defaultStartDate : workItemStartList[0]
        const startDate = moment(firstStartDate).isBefore(moment(defaultStartDate)) ? firstStartDate : defaultStartDate

        return [...Array(length).keys()].map(i => {
            const start = moment(new Date(startDate === null ? newYear : startDate)).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
            start.add(Math.floor(i / 3), 'months').date(10 * (i % 3) + 1)
            const end = moment(new Date(startDate === null ? newYear : startDate)).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
            const j = i + 1
            end.add(Math.floor(j / 3), 'months').date(10 * (j % 3) + 1).subtract(1, 'days')
            return { startDate: start.toDate(), endDate: end.toDate() };
        })
    }

    get selectedWorkWithSeason(): {
        planType: string,
        workItemName: string | undefined,
        agoOrLater: string | null,
        days: number | null,
        seasonList: Season[]
    }[] {
        return this.newWorkDetails.map(sw => ({
            planType: sw.plan_type,
            workItemName: this.newWorkDetails.find(nwd => nwd.work_item.id === sw.selected_work_item_id)?.work_item.name,
            agoOrLater: sw.ago_or_later,
            days: sw.days,
            seasonList: this.seasonList.map(d => ({
                tagId: sw.work_item.id + d.startDate.getFullYear() + d.startDate.getMonth() + d.startDate.getDate(),
                selectedWorkId: sw.work_item.id,
                startDate: d.startDate,
                endDate: d.endDate,
                withinPeriod: moment(sw.working_start_date).isSameOrBefore(moment(d.endDate)) && moment(sw.working_end_date).isSameOrAfter(moment(d.startDate)),
                firstSeason: moment(sw.working_start_date).isSameOrAfter(moment(d.startDate)) && moment(sw.working_start_date).isSameOrBefore(moment(d.endDate)),
            }))
        }))
    }

    formatWorkPeriodDate(date: Date): string {
        return moment(date).format("YYYY-MM-DD")
    }

    get refs(): any {
        return this.$refs;
    }

    isMouse = false
    index = 0;
    currentSelectedWorkPeriod: Element | null = null

    touchStart(s: Season, idx: number, planType: string): void {
        if (planType === 'PERIOD') {
            // 対象行の要素を確保
            this.currentSelectedWorkPeriod = this.refs["work_period_table"][idx]
            this.isMouse = true
            this.index = idx

            this.newWorkDetails[idx]['working_start_date'] = moment(s.startDate).format('YYYY-MM-DD')
            this.newWorkDetails[idx]['working_end_date'] = moment(s.endDate).format('YYYY-MM-DD')
        }
    }

    dragger(e: MouseEvent): void {
        if (this.isMouse) {
            // マウスの現在座標から一番近い要素を特定
            let minDistance: number = Number.MAX_VALUE
            let closestElement: Element | null = null;
            (this.currentSelectedWorkPeriod?.childNodes as NodeListOf<Element>).forEach((node, idx) => {
                const rect: DOMRect = node.getBoundingClientRect()

                // マウスポインタからの直線距離
                const distance = Math.sqrt(Math.pow(e.pageX - rect.left, 2) + Math.pow(e.pageY - rect.top, 2))
                if (distance < minDistance) {
                    minDistance = distance
                    closestElement = node
                }
            })

            // closestElementからendDateを抽出
            const workPeriod = this.newWorkDetails[this.index]

            const startDate: string | null = workPeriod['working_start_date']
            const endDate: string = closestElement!.getAttribute("date")!;

            workPeriod['working_start_date'] = startDate
            workPeriod['working_end_date'] = endDate
        }
    }

    touchEnd(): void {
        if (this.isMouse) {
            this.newWorkDetails = this.sortWorkDetails(this.newWorkDetails)

            this.isMouse = false
        }
    }

    sortWorkDetails(workDetails: SelectedWorkDetail[]): SelectedWorkDetail[] {
        const updatedWorkDetail = workDetails.map(wd => {
            if (wd.plan_type === 'ELAPSED_DAYS' && wd.selected_work_item_id !== null && wd.days !== null) {
                const selectedWork = workDetails.find(nwd => nwd.work_item.id === wd.selected_work_item_id)
                const agoOrLater = wd.ago_or_later === '日前' ? -1 : 1
                const start = moment(selectedWork?.working_start_date).add(agoOrLater * wd.days, 'days').format('YYYY-MM-DD')
                const end = moment(selectedWork?.working_end_date).add(agoOrLater * wd.days, 'days').format('YYYY-MM-DD')
                return { ...wd, working_start_date: start, working_end_date: end }
            } else {
                return wd
            }
        })
        updatedWorkDetail.sort((prev, after) => {
            return moment(prev.working_start_date).isBefore(moment(after.working_start_date)) ? -1 : 1
        })

        return updatedWorkDetail
    }

    // workItemImage: WorkItemImage = {
    //     id: '',
    //     url: '',
    //     file: null
    // }

    selectWorkItemFile() {
        document.getElementById('work-item-file-input')?.click()
    }

    addImage(e: any): void {
        const files = e.target.files || e.dataTransfer.files;
        if (!files || files.length == 0 || this.$apiClient.isSizeOver(files)) {
            return
        }

        if (this.$apiClient.isImageFile(files[0].name)) {
            this.$apiClient.getBase64(files[0]).then((image: any) => {
                const workItemImage = {
                    id: '',
                    url: image.base64 as string,
                    file: files[0]
                }
                this.editingImages.push(workItemImage)
            }).catch(() => {
                window.alert('画像ファイルを選択してください')
            });
        } else {
            window.alert('画像ファイルを選択してください')
        }
    }

    deleteImage(image: WorkItemImage, index: number): void {
        this.editingImages.splice(index, 1)
        // if(image.id) {
        //     this.deletedImages.push(image)
        // }
    }

    savePlanting(): any {
        const deletedWorkItem = this.initWorkItemsId
            .filter(iwii => this.newWorkDetails.find(nwd => nwd.work_item.id === iwii) === undefined)
            .map(iwii => ({ work_item: { id: iwii, name: '' }, update_type: 'DELETE' }))
        this.uploadImages = []
        this.deletedImages = []
        this.newWorkDetails.forEach((work) => {
            const workItemId = work.work_item.id
            const images = this.editingImageMap.get(workItemId)
            if (images) {
                // idが空のファイルはアップロード対象
                this.uploadImages = this.uploadImages.concat(images.filter((image) => image.id == '').map((image) => {
                    return {
                        workItemId: workItemId,
                        file: image.file
                    }
                }))
                // 元のリストからidが消えているものは削除対象
                const initImage = this.initWorkItemImages.find(item => item.workItemId == workItemId)
                if(initImage && initImage.fileId.length > 0) {
                    this.deletedImages = this.deletedImages.concat(
                        initImage.fileId.filter(
                            (fileId) => !images.some(i => i.id == fileId)
                        ).map((fileId) => {
                            return {
                                workItemId: workItemId,
                                fileId: fileId
                            }
                        })
                    )
                }

            }
        })

        // 削除された作業項目に添付されていたファイルを削除対象に追加
        const deletedWorkItemImages = this.initWorkItemImages.filter(
            iwii => deletedWorkItem.some(
                dwi => iwii.workItemId == dwi.work_item.id
            )
        )
        deletedWorkItemImages.forEach(dwii => {
            dwii.fileId.forEach(fileId => {
                const image = {
                    workItemId: dwii.workItemId,
                    fileId: fileId
                }
                this.deletedImages.push(image)
            })
        })

        return { ...this.planting, selected_works: [...this.newWorkDetails, ...deletedWorkItem] }
    }

    validationCultivationPlan(): boolean {
        let errmsg: string[] = [] 
        if (this.planting.work_start_date == null || this.planting.work_end_date == null) {
            if (this.planting.work_start_date && this.planting.work_end_date == null) {
                errmsg.push("作業期間の終了日を設定してください。")
            } else if (this.planting.work_start_date == null && this.planting.work_end_date) {
                errmsg.push("作業期間の開始日を設定してください。")
            }
        } else if (this.planting.work_start_date > this.planting.work_end_date) {
            errmsg.push("作業期間の開始日は終了日以前で設定してください。")
        }

        if (this.planting.total_yield != null && this.planting.total_yield < 0) {
            errmsg.push("計画収量は0以上を入力してください。")
        }

        if (this.planting.shipping_start_date == null || this.planting.shipping_end_date == null) {
            if (this.planting.shipping_start_date && this.planting.shipping_end_date == null) {
                errmsg.push("出荷期間の終了日を設定してください。")
            } else if (this.planting.shipping_start_date == null && this.planting.shipping_end_date) {
                errmsg.push("出荷期間の開始日を設定してください。")
            }
        } else if (this.planting.shipping_start_date > this.planting.shipping_end_date) {
            errmsg.push("出荷期間の開始日は終了日以前で設定してください。")
        }

        this.newWorkDetails.forEach(work => {
            const detailErrMsg = this.validationWorkItemDetail(work)
            if(detailErrMsg.length > 0) {
                errmsg = errmsg.concat(detailErrMsg.join()+`(作業項目：${work.work_item.name})`)
            }
        })

        if(errmsg.length > 0) {
            window.alert(errmsg.join('\n'))
            return false
        }

        return true
    }

    setDefaultTerm(type: string): void {
        const today = new Date();
        const nextYear = new Date()
        nextYear.setFullYear(today.getFullYear() + 1)

        switch (type) {
            case 'cultivate':
                this.planting.work_start_date = this.formatWorkPeriodDate(today)
                this.planting.work_end_date = this.formatWorkPeriodDate(nextYear)
                break
            case 'shipping':
                this.planting.shipping_start_date = this.formatWorkPeriodDate(today)
                this.planting.shipping_end_date = this.formatWorkPeriodDate(nextYear)
                break
        }

    }
    clearTerm(type: string): void {
        switch (type) {
            case 'cultivate':
                this.planting.work_start_date = null
                this.planting.work_end_date = null
                break
            case 'shipping':
                this.planting.shipping_start_date = null
                this.planting.shipping_end_date = null
                break
        }
    }

    clearCultivationPlan(): void {
        if (window.confirm("すべての栽培計画をクリアします。よろしいですか。")) {
            this.planting.work_start_date = null
            this.planting.work_end_date = null
            this.planting.selected_works = []
            this.planting.per_type = this.defaultPerType
            this.planting.total_yield = null
            this.planting.weight_unit = this.defaultWeightUnit
            this.planting.shipping_start_date = null
            this.planting.shipping_end_date = null
            this.newWorkDetails = []
            this.editingImageMap = new Map()
            this.initWorkItemImages.forEach((image) => this.editingImageMap.set(image.workItemId, []))
        }
    }

    onChangeTotalTime(selectedWork: SelectedWorkDetail, e: InputEvent): void {
        this.newWorkDetails = this.newWorkDetails.map(nwd => (
            nwd.work_item.id === selectedWork.work_item.id
                ? { ...nwd, total_time: e.target instanceof HTMLInputElement ? e.target.valueAsNumber : nwd.total_time }
                : nwd
        ))
    }

    onClickSettingType(e: Event): void {
        const click = e.target instanceof HTMLInputElement ? e.target.value : '合計時間'
        if (
            this.editingDetail.plan_type === 'PERIOD'
        ) {
            const startFrom = moment(this.editingDetail.working_start_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            const startTo = moment(this.editingDetail.working_start_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            const endFrom = moment(this.editingDetail.working_end_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            const endTo = moment(this.editingDetail.working_end_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
            if (click === '月別') {
                startTo.date(1)
                endTo.add(1, 'months').date(1).subtract(1, 'days')
            } else if (click === '旬別') {
                if (startTo.date() <= 10) {
                    startTo.date(1)
                } else if (startTo.date() <= 20) {
                    startTo.date(11)
                } else {
                    startTo.date(21)
                }
                if (endTo.date() >= 21) {
                    endTo.add(1, 'months').date(1).subtract(1, 'days')
                } else if (endTo.date() >= 11) {
                    endTo.date(20)
                } else {
                    endTo.date(10)
                }
            }

            const list = []
            if (this.editingDetail.working_start_date !== null && startFrom.date() !== startTo.date()) {
                list.push('\n作業期間開始： ' + startFrom.format('YYYY-MM-DD') + ' => ' + startTo.format('YYYY-MM-DD'))
            }
            if (this.editingDetail.working_end_date !== null && endFrom.date() !== endTo.date()) {
                list.push('\n作業期間終了： ' + endFrom.format('YYYY-MM-DD') + ' => ' + endTo.format('YYYY-MM-DD'))
            }

            if (list.length !== 0) {
                const result = confirm(
                    "作業時間設定方法で\"" + click + "で設定\"が選択されたので、作業期間が下記のように変更されます。よろしいでしょうか？\n"
                    + list.reduce((a, b) => a + b)
                )
                if (result) {
                    this.editingDetail.working_start_date = startTo.format('YYYY-MM-DD')
                    this.editingDetail.working_end_date = endTo.format('YYYY-MM-DD')
                } else {
                    e.preventDefault()
                }
            }
        }
    }

    onChangeSettingType(): void {
        this.editingDetail.time_allocation = this.culcTimeAllocations()
    }

    onChangeWeightUnit(e: Event): void {
        if(e.target instanceof HTMLSelectElement) {
            const target = e.target
            const weitUnit = this.weightUnits.find(wu => wu.id === target.value)
            this.planting.weight_unit = weitUnit ? weitUnit : this.defaultWeightUnit
        }
    }

    onSelectWorkStart(date: Date): void {
        const ta = this.editingDetail.time_allocation
        const startFrom = moment(this.editingDetail.working_start_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        const startTo = moment(date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        if (this.editingDetail.setting_type === '月別') {
            if (startTo.isBefore(startFrom)) {
                while (startTo.isBefore(startFrom)) {
                    startFrom.subtract(1, 'months')
                    ta.unshift({ working_period: startFrom.format('YYYY年MM月'), working_hour: 0 })
                }
            } else {
                const diff = startTo.diff(startFrom, 'month')
                ta.splice(0, diff)
            }
        } else if (this.editingDetail.setting_type === '旬別') {
            if (startTo.isBefore(startFrom)) {
                while (startTo.isBefore(startFrom)) {
                    if (startFrom.date() === 1) {
                        startFrom.subtract(1, 'months').date(21)
                        ta.unshift({ working_period: startFrom.format('YYYY年MM月下旬'), working_hour: 0 })
                    } else if (startFrom.date() === 11) {
                        startFrom.date(1)
                        ta.unshift({ working_period: startFrom.format('YYYY年MM月上旬'), working_hour: 0 })
                    } else if (startFrom.date() === 21) {
                        startFrom.date(11)
                        ta.unshift({ working_period: startFrom.format('YYYY年MM月中旬'), working_hour: 0 })
                    }
                }
            } else {
                const diff = startTo.diff(startFrom, 'month') * 3 + Math.floor(startTo.date() / 10) - Math.floor(startFrom.date() / 10)
                ta.splice(0, diff)
            }
        }
        this.editingDetail.time_allocation = ta
        this.editingDetail.total_time = ta.map(t => t.working_hour).reduce((sum, t) => sum + t)
    }

    onSelectWorkEnd(date: Date): void {
        const ta = this.editingDetail.time_allocation
        const endFrom = moment(this.editingDetail.working_end_date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        const endTo = moment(date).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        if (this.editingDetail.setting_type === '月別') {
            if (endTo.isAfter(endFrom)) {
                while (endTo.isAfter(endFrom)) {
                    endFrom.add(2, 'months').date(1).subtract(1, 'days')
                    ta.push({ working_period: endFrom.format('YYYY年MM月'), working_hour: 0 })
                }
            } else {
                const diff = endFrom.diff(endTo, 'months')
                ta.splice(ta.length - diff, diff)
            }
        } else if (this.editingDetail.setting_type === '旬別') {
            if (endTo.isAfter(endFrom)) {
                while (endTo.isAfter(endFrom)) {
                    if (endFrom.date() === 10) {
                        endFrom.date(20)
                        ta.push({ working_period: endFrom.format('YYYY年MM月中旬'), working_hour: 0 })
                    } else if (endFrom.date() === 20) {
                        endFrom.add(1, 'months').date(1).subtract(1, 'days')
                        ta.push({ working_period: endFrom.format('YYYY年MM月下旬'), working_hour: 0 })
                    } else {
                        endFrom.add(1, 'months').date(10)
                        ta.unshift({ working_period: endFrom.format('YYYY年MM月上旬'), working_hour: 0 })
                    }
                }
            } else {
                const diff = endFrom.diff(endTo, 'month') * 3 + Math.floor(endFrom.date() / 10) - Math.floor(endTo.date() / 10)
                ta.splice(ta.length - diff, diff)
            }
        }
        this.editingDetail.time_allocation = ta
        this.editingDetail.total_time = ta.map(t => t.working_hour).reduce((sum, t) => sum + t)
    }

    onChangeTimeAllocation(timeAllocation: { working_period: string, working_hour: number }, e: InputEvent): void {
        const timeAllocations = this.editingDetail.time_allocation.map(ta => (
            ta.working_period === timeAllocation.working_period
                ? { ...ta, working_hour: e.target instanceof HTMLInputElement ? e.target.valueAsNumber : ta.working_hour }
                : ta
        ))
        this.editingDetail.time_allocation = timeAllocations
        this.editingDetail.total_time = timeAllocations.map(ta => ta.working_hour).reduce((sum, ta) => ta ? sum + ta : sum)
    }

    // 作付コピーモーダル
    showPlantingCopyModal(): void {
        this.showSelectPlanting = true;
    }

    catchPlanting(plantingId: string): void {
        this.editingImageMap = new Map()
        this.initWorkItemImages.forEach((image) => this.editingImageMap.set(image.workItemId, []))
        this.getPlantingDetail(plantingId)
    }

    closeSelectPlantingModal(): void {
        this.showSelectPlanting = false;
    }

    @Watch('planting')
    updateSelectedWorks(): void {
        this.init()
    }
}
